/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { useMutation, useQuery } from "@apollo/client";
import {
  CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, Grid,
} from "@mui/material";
import React from "react";
import { Field, FieldProps, Form, Formik, FormikConfig, FormikConsumer } from "formik";
import * as yup from "yup";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { Button, Card } from "components/haven-design";
import { CsvImportConfigInput, ImporterConfigQuery, ImportLotteryCSVMutation, ImportLotteryCSVMutationVariables } from "types/gql-types";
import { CheckboxWithErrorLabel } from "components/haven-design/form/checkbox";
import ErrorBoundary from "../interest_imports/components/boundary";
import Upload from "../interest_imports/components/upload";
import { onUploadCSV } from "../interest_imports/components/utils";
import { ImporterConfig, ImportLotteryMutation } from "./queries";

const schema = yup.object({
  interestList: yup.object({
    id: yup.string().required(),
  }).required(),
  config: yup.object({
    dryRun: yup.bool().default(false),
    allowNulls: yup.bool().default(false),
    columnMappings: yup.array(yup.object({
      csvColumn: yup.string(),
      havenColumn: yup.string().required(),
    })).required(),
  }).required(),
});

type Values = yup.InferType<typeof schema>

const initialValuesFor = (config: ImporterConfigQuery["csvImporterConfig"], headers: string[]): Values => {
  // Prefill column names if possible:
  const columnMappings = config.map(({ column: havenColumn }) => {
    const csvColumn = headers.includes(havenColumn) ? havenColumn : "";
    return ({ havenColumn, csvColumn });
  });

  return {
    interestList: {
      id: "",
    },
    config: {
      dryRun: false,
      allowNulls: false,
      columnMappings,
    },
  };
};

const Lottery: React.FC = () => {
  const [interestList, setInterestList] = React.useState<ImporterConfigQuery["interestLists"][number] | null>(null);
  const [csvFile, setCsvFile] = React.useState<{ headers: string[], file: null | File }>({
    file: null,
    headers: [],
  });

  const { loading, data, error } = useQuery<ImporterConfigQuery>(ImporterConfig);

  const [importLottery, importLotteryResults] = useMutation<
  ImportLotteryCSVMutation,
  ImportLotteryCSVMutationVariables
  >(ImportLotteryMutation);

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (error || !data?.interestLists) return <p>There was an error fetching the interest lists</p>;

  const handleUpload = onUploadCSV((result, file) => {
    const [headers] = result.data;

    if (!headers) {
      throw new Error("insufficient data to derive headers from.");
    }

    setCsvFile({ file: null, headers: [] });
    setCsvFile({ file, headers });
  });

  const importCSV: FormikConfig<Values>["onSubmit"] = async ({ config }) => {
    const csv = await csvFile.file?.text() || "";
    if (!interestList) return;

    const cleanConfig = {
      ...config,
      // This is to allow lottery to be optional
      columnMappings: config.columnMappings.filter(cm => cm.csvColumn),
    };

    await importLottery({
      variables: {
        id: interestList.id,
        config: cleanConfig as CsvImportConfigInput,
        csv,
      },
    });
  };
  const warnings = (importLotteryResults.data?.importCsv?.messages || []);
  const errors = (importLotteryResults.error?.graphQLErrors || []);

  return (
    <ErrorBoundary>
      <Card>
        <Typography variant="h5" gutterBottom>Import Lottery Spreadsheet</Typography>

        <Typography variant="h6" gutterBottom>Select a CSV from your computer</Typography>
        <Typography variant="body1">
          CSVs larger than 500 rows might fail or timeout.{" "}
          Only have an Excel spreadsheet?{" "}
          <a
            href="https://support.office.com/en-us/article/Import-or-export-text-txt-or-csv-files-5250ac4c-663c-47ce-937b-339e391393ba"
            target="_blank" rel="noreferrer"
          >
            Export it as a CSV
          </a>
          .
        </Typography>

        <Upload onSelect={handleUpload} />

        <Grid container spacing={2}>
          <Grid size={{ sm: 12, md: 6 }}>
            <SelectAutocomplete
              required
              margin="dense"
              size="small"
              label="Interest List"
              getOptionLabel={(option) => (option?.id ? `${option?.name} (${option?.organization?.name})` : "")}
              options={data.interestLists}
              value={interestList}
              onChange={(_, value) => setInterestList(value)}
            />
          </Grid>
          {(
            <Formik
              enableReinitialize
              initialValues={initialValuesFor(data.csvImporterConfig, csvFile.headers)}
              onSubmit={importCSV}
            >
              <Grid size={{ sm: 12 }}>
                <Form>

                  <Grid size={{ sm: 12, md: 6 }}>
                    <CheckboxWithErrorLabel label="Dry Run?" name="config.dryRun" />
                    <CheckboxWithErrorLabel label="Allow NULL for Lottery Number?" name="config.allowNulls" />
                  </Grid>

                  <Grid size={{ sm: 12 }}>
                    <Typography variant="h6" gutterBottom>Column name mapping</Typography>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Haven Column</TableCell>
                            <TableCell width={"33%"}>CSV Column</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {data.csvImporterConfig.map((columnConfig, index) => (
                            <TableRow key={columnConfig.column}>
                              <TableCell width={"25%"}>
                                {columnConfig.required ? <strong>{columnConfig.label}*</strong> : columnConfig.label}
                                {columnConfig.description && <p>{columnConfig.description}</p>}
                              </TableCell>
                              <TableCell>
                                <Field name={`config.columnMappings[${index}].csvColumn`}>
                                  {({ field, form }: FieldProps<string>) => (
                                    <SelectAutocomplete
                                      margin="dense"
                                      size="small"
                                      autoComplete
                                      options={[...csvFile.headers]}
                                      error={!field.value && !!columnConfig.required}
                                      {...field}
                                      value={field.value}
                                      onChange={(_, value) => form.setFieldValue(field.name, value)}
                                    />
                                  )}
                                </Field>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid size={{ sm: 12 }}>
                    {importLotteryResults.loading ? (
                      <>
                        <CircularProgress /> Importing ...
                      </>
                    ) : (
                      <FormikConsumer>
                        {({ isValid }) => (
                          <Button type="submit" color="primary" style={{ marginTop: 16 }} disabled={!isValid || !interestList || !csvFile.file}>
                          Import
                          </Button>
                        )}
                      </FormikConsumer>
                    )}
                  </Grid>
                </Form>
              </Grid>
            </Formik>
          )}
        </Grid>

        {importLotteryResults.called && !importLotteryResults.loading && (
          <>
            <Typography variant="h6" gutterBottom>Import Results</Typography>

            {importLotteryResults.data?.importCsv?.success && (
              <Typography variant="body1">Import Successful!</Typography>
            )}

            {importLotteryResults.error && (
              <Typography variant="body1">
                Something went wrong, please check the
                above validation issues as well as review
                the table below.
              </Typography>
            )}

            {!!warnings.length && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={"25%"}>CSV Row #</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {warnings.map(({ row, details }, index) => (
                      <TableRow key={index}>
                        <TableCell width={"25%"}>
                          {row}
                        </TableCell>
                        <TableCell>
                          {details}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!!errors.length && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={"25%"}>CSV Row #</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {errors.map((errorItem, index) => (<TableRow key={index}>
                      <TableCell width={"25%"}>
                        {/* @ts-ignore */}
                        {errorItem.row}
                      </TableCell>
                      <TableCell>
                        {/* @ts-ignore */}
                        {(errorItem.details && errorItem.details.row && errorItem.details.row[0]) || errorItem.message}
                      </TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Card>
    </ErrorBoundary>
  );
};

export default Lottery;
